import type { DealCard, DealFilters } from '#shared/types/deal'
import type { PaginatedResponse } from '#shared/types/api'

export function useDeals(filters?: Ref<DealFilters> | DealFilters) {
  const config = useRuntimeConfig()

  return useFetch<PaginatedResponse<DealCard>>('/deals', {
    baseURL: config.public.apiBaseUrl,
    query: filters,
    // Nuxt 4: shallow reactivity por default (melhor performance)
    key: 'deals-list',
  })
}

export function useDeal(slug: string) {
  const config = useRuntimeConfig()

  return useFetch<DealCard>(`/deals/${slug}`, {
    baseURL: config.public.apiBaseUrl,
    key: `deal-${slug}`,
  })
}

export function useInfiniteDeals(initialFilters?: DealFilters, initialPageData?: PaginatedResponse<DealCard> | null) {
  const config = useRuntimeConfig()
  const deals = ref<DealCard[]>(initialPageData?.items ?? [])
  const page = ref(initialPageData ? 2 : 1)
  const hasMore = ref(initialPageData?.hasMore ?? true)
  const loading = ref(false)
  const filters = ref<DealFilters>(initialFilters ?? {})

  async function loadMore() {
    if (loading.value || !hasMore.value) return
    loading.value = true
    try {
      const data = await $fetch<PaginatedResponse<DealCard>>('/deals', {
        baseURL: config.public.apiBaseUrl,
        query: { ...filters.value, page: page.value, limit: 20 },
      })
      deals.value.push(...data.items)
      hasMore.value = data.hasMore
      page.value++
    }
    finally {
      loading.value = false
    }
  }

  async function reset(newFilters?: DealFilters, nextPageData?: PaginatedResponse<DealCard> | null) {
    deals.value = nextPageData?.items ?? []
    page.value = nextPageData ? 2 : 1
    hasMore.value = nextPageData?.hasMore ?? true
    if (newFilters) filters.value = newFilters
    if (!nextPageData) {
      await loadMore()
    }
  }

  return { deals: readonly(deals), loadMore, reset, hasMore: readonly(hasMore), loading: readonly(loading) }
}
